export interface User {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  githubUsername?: string; // camelCase (padrão)
  gitHubUsername?: string; // formato retornado pela API
  role?: string; // 'owner' | 'admin' | 'member'
  createdAt?: string;
  permissions?: Record<string, boolean>; // Cache de permissões
}

export enum Permission {
  // Visualização
  ViewDashboard = 'view:dashboard',
  ViewEngineeringMetrics = 'view:engineering-metrics',
  ViewReliabilityMetrics = 'view:reliability-metrics',
  ViewContributors = 'view:contributors',
  ViewRepositories = 'view:repositories',
  ViewIncidents = 'view:incidents',
  ViewPlanning = 'view:planning',
  CreatePlanning = 'create:planning',
  EditPlanning = 'edit:planning',
  DeletePlanning = 'delete:planning',

  // Configurações
  ManageIntegrations = 'manage:integrations',
  ManageRepositories = 'manage:repositories',
  ManageExtractionConfig = 'manage:extraction-config',
  ManageManualSync = 'manage:manual-sync',
  
  // Gestão de Usuários
  ViewMembers = 'view:members',
  InviteMembers = 'invite:members',
  RemoveMembers = 'remove:members',
  ChangeMemberRoles = 'change:member-roles',
  AssociateGitHubAccounts = 'associate:github-accounts',
  ManagePermissions = 'manage:permissions',
  
  // Organização
  EditOrganization = 'edit:organization',
  DeleteOrganization = 'delete:organization',
  TransferOwnership = 'transfer:ownership',
  
  // Dados
  GenerateRetroactiveHistory = 'generate:retroactive-history',
  ExportData = 'export:data',
  
  // Incidentes
  CreateIncident = 'create:incident',
  EditIncident = 'edit:incident',
  DeleteIncident = 'delete:incident',
  CloseIncident = 'close:incident',
}

export interface RoleTemplate {
  id: string;
  organizationId: string;
  roleName: 'owner' | 'admin' | 'member';
  displayName: string;
  permissions: Record<string, boolean>;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserPermissionOverride {
  id: string;
  userId: string;
  organizationId: string;
  permissions: Record<string, boolean | null>; // null = herdar
  updatedAt: string;
  updatedBy: string;
}

export interface Organization {
  id: string;
  name: string;
  description?: string;
  ownerId: string;
  createdAt: string;
  gitHubToken?: string;
  gitHubClientId?: string;
  gitHubClientSecret?: string;
  gitHubCallbackUrl?: string;
  gitHubWebhookSecret?: string;
}

export interface Membership {
  id: string;
  organizationId: string;
  userId: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  invitedBy?: string;
  user?: User;
}

export interface Invitation {
  id: string;
  organizationId: string;
  email: string;
  code: string;
  status: 'pending' | 'used' | 'cancelled';
  invitedBy: string;
  githubUsername?: string;
  createdAt: string;
  expiresAt: string;
  usedAt?: string;
  cancelledAt?: string;
}

export interface Repository {
  id: string;
  organizationId: string;
  name: string;
  owner: string;
  fullName: string;
  selected: boolean;
  webhookId?: string;
  lastSyncDate?: string;
  lastSyncType?: 'webhook' | 'manual';
  lastSyncStatus?: 'success' | 'failed' | 'in_progress';
}

export interface PullRequest {
  id: string;
  organizationId: string;
  repositoryId: string;
  number: number;
  state: string;
  createdAt: string;
  mergedAt?: string;
  author: string;
  reviews: Review[];
  filesChanged: number;
  linesChanged: number;
  firstCommitDate?: string;
  baseRef?: string;
  headRef?: string;
  environment?: string;
}

export interface PullRequestEnvironmentConfig {
  id?: string;
  organizationId: string;
  repositoryId?: string;
  baseBranchToEnvironment: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
  isGlobal?: boolean;
}

export interface Review {
  id: string;
  reviewer: string;
  state: string;
  submittedAt: string;
}

export interface Deployment {
  id: string;
  organizationId: string;
  repositoryId: string;
  environment: string;
  status: string;
  createdAt: string;
  workflowRunId: string;
  /** Nome do deployment (job/workflow). */
  jobName?: string;
  /** URL do deployment (workflow run ou target_url). */
  url?: string;
  /** URL do job no GitHub Actions. */
  jobUrl?: string;
}

export interface Metrics {
  meanCycleTime?: number;
  meanLeadTime?: number;
  meanReviewTime?: number;
  throughput?: number;
  reviewCoverage?: number;
  prRateOnDay?: number;
  meanPRSize?: number;
  mergePRRate?: number;
}

export interface DeployMetrics {
  dev: DeployEnvironmentMetrics;
  prod: DeployEnvironmentMetrics;
}

export interface DeployEnvironmentMetrics {
  deployFrequency: number;
  deployByDay: number;
  deployByWeek: number;
  deploySuccessRate: number;
}

export interface ContributorMetrics {
  contributor: string;
  prsOpened: number;
  prsMerged: number;
  mergeRate: number;
  reviews: number;
  reviewsApproved: number;
  reviewsChangesRequested: number;
  prsReviewed: number;
  approvalRate: number;
  avgPRSize: number;
  totalLinesChanged: number;
  totalFilesChanged: number;
  totalContributions: number;
  opindex: number;
  opindexVolume: number;
  opindexQuality: number;
  opindexComplexity: number;
  opindexEngagement: number;
  opindexPercentile: number;
  opindexHistorical: number;
}

export interface OpindexHistoryPoint {
  id: string;
  contributor: string;
  organizationId: string;
  repositoryId?: string;
  opindex: number;
  opindexVolume: number;
  opindexQuality: number;
  opindexComplexity: number;
  opindexEngagement: number;
  date: string;
  calculatedAt: string;
}

export interface GitHubContributor {
  username: string;
  prsOpened: number;
  reviews: number;
  totalContributions: number;
  isAvailable: boolean;
  isCurrentUser?: boolean;
}

export interface RepositoryMetrics {
  repositoryId: string;
  repositoryName: string;
  fullName: string;
  prMetrics: Metrics;
  deployMetrics: DeployMetrics;
  uniqueContributors: number;
  lastActivity?: string;
  totalPRs: number;
  openPRs: number;
}

export interface WorkflowRun {
  id: string;
  organizationId: string;
  repositoryId: string;
  workflowName: string;
  status: string;
  conclusion: string;
  environment: string;
  branch: string;
  pullRequestNumber?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  actor: string;
  workflowRunUrl: string;
}

export interface Notification {
  id: string;
  userId?: string;
  organizationId?: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  notificationType?: 'info' | 'success' | 'warning' | 'error';
  actionUrl?: string; // URL para redirecionar quando clicar na notificação
}

export interface EnvironmentPatternConfig {
  identificationType: 'environment' | 'branch' | 'workflow' | 'job';
  productionPatterns: string[];
  developmentPatterns: string[];
  useRegex: boolean;
}

export interface DeploymentExtractionConfig {
  id?: string;
  organizationId: string;
  repositoryId?: string;
  environmentPatterns: EnvironmentPatternConfig;
  statusMapping: Record<string, string>;
  fieldMappings: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
  isGlobal?: boolean;
}

export interface WorkflowRunExtractionConfig {
  id?: string;
  organizationId: string;
  repositoryId?: string;
  environmentPatterns: EnvironmentPatternConfig;
  deployWorkflowPatterns: string[];
  statusMapping: Record<string, string>;
  conclusionMapping: Record<string, string>;
  fieldMappings: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
  isGlobal?: boolean;
}

export interface SyncRequest {
  id: string;
  organizationId: string;
  repositoryId: string;
  userId: string;
  syncDate: string; // ISO date string
  status: 'enqueued' | 'in_progress' | 'success' | 'failed';
  errorMessage?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  pullRequestsCount?: number;
  deploymentsCount?: number;
  workflowRunsCount?: number;
}

export interface Incident {
  id: string;
  organizationId: string;
  title: string;
  createdAt: string;
  startedAt?: string;
  detectedAt?: string;
  finishedAt?: string;
  reporterId: string;
  status: 'ativo' | 'estável' | 'resolvido';
  severity: 'sev-1' | 'sev-2' | 'sev-3' | 'sev-4';
  teamIds: string[];
  detectionMethodIds: string[];
  repositoryIds: string[];
  whatHappened?: string;
  whyHappened?: string;
  relatedDeploymentId?: string;
  detectionTime?: string; // ISO duration string
  incidentDuration?: string; // ISO duration string
  // Compatibilidade com formato antigo
  teamId?: string;
  detectionMethodId?: string;
}

export interface Impact {
  id: string;
  incidentId: string;
  description: string;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  duration?: string; // ISO duration string
}

export interface Postmortem {
  id: string;
  incidentId: string;
  reporterId: string;
  createdAt: string;
  detection?: string;
  rootCause?: string;
  lessonsLearned?: string;
  actionsTaken?: string;
  actionItems?: string | string[];
}

export interface Team {
  id: string;
  organizationId: string;
  name: string;
  createdAt: string;
}

export interface DetectionMethod {
  id: string;
  organizationId: string;
  name: string;
  createdAt: string;
}

export interface IncidentWithDetails {
  incident: Incident;
  impacts: Impact[];
  postmortem?: Postmortem;
}

export interface IncidentMetrics {
  mtbf?: string; // ISO duration string
  mttrResolution?: string; // ISO duration string
  mttrRecover?: string; // ISO duration string
  mttd?: string; // ISO duration string
  changeFailureRate?: number; // percentage
}

export interface IncidentMetricsSummary {
  mtbf?: string;
  mttrResolution?: string;
  mttrRecover?: string;
  mttd?: string;
  changeFailureRate?: number;
}

export interface IncidentTimeline {
  id: string;
  incidentId: string;
  action: string;
  userId: string;
  userName: string;
  createdAt: string;
  description?: string;
  changes?: Record<string, any>;
}
